import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'
import { Form } from '../../../components/form/FormComponents'
import CardEmbed from '../../../components/cardView/CardEmbed'

const PageWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`

const Body = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1rem 1.5rem;
`

// The card's width, so the chart of accounts lines up with its sections.
const Column = styled.div`
    max-width: 756px;
`

const Label = styled.label`
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: ${p => p.theme.font.label};
    margin-bottom: 0.4rem;
`

const Select = styled.select`
    width: 100%;
    padding: 0.5rem 0.6rem;
    border: 1px solid ${p => p.theme.form.border};
    border-radius: 0.375rem;
    background: ${p => p.theme.form.background};
    color: ${p => p.theme.font.color};
    font-size: 0.9rem;
    margin-bottom: 1rem;
`

const Button = styled.button`
    padding: 0.5rem 1.2rem;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    border-radius: 0.375rem;
    cursor: pointer;
    background: ${p => p.theme.btn.primary};
    color: ${p => p.theme.btn.font};

    &:disabled { opacity: 0.5; cursor: default; }
    &:not(:disabled):hover { opacity: 0.88; }
`

const StatusBox = styled.div`
    padding: 0.85rem 1rem;
    border: 1px solid ${p => p.theme.form.border};
    border-radius: 0.375rem;
    margin-bottom: 1rem;
`

const ErrorText = styled.div`
    color: #c0392b;
    font-size: 0.85rem;
    margin-top: 0.5rem;
`

/*
 * Accounting › Settings — the one place for the accounting settings: the
 * accountingSettings card (VAT profile, One Stop Shop, deduction rates), and
 * the chart of accounts the org was set up with. The card is the page's
 * heading and its Save; the chart sits below it once it exists, and above it
 * while it still has to be created, since nothing posts without one.
 */
const AccountingSettingsPage = () => {
    const { getMe } = useApi()
    const { data, isLoading } = useQuery({
        queryKey: ['coaTemplate', 'status'],
        queryFn: () => getMe('coaTemplate/status'),
    })
    const applied = !!data?.data?.applied

    return (
        <PageWrap>
            <Body>
                <Column>
                    {!isLoading && !applied && <ChartOfAccounts />}
                    <CardEmbed type="accountingSettingsCard" />
                    {applied && <ChartOfAccounts />}
                </Column>
            </Body>
        </PageWrap>
    )
}

// The org's Chart of Accounts is not seeded automatically for orgs created
// before this feature existed (only new-org creation triggers cloneCoaTemplate —
// see orgRoutes.js). This lets the user pick a platform template and create
// the accounts once, for orgs that never got one.
const ChartOfAccounts = () => {
    const { t, m } = useTranslation()
    const { getMe, postMe } = useApi()
    const queryClient = useQueryClient()
    const [selectedTemplateId, setSelectedTemplateId] = useState('')

    const { data: statusData, isLoading: statusLoading } = useQuery({
        queryKey: ['coaTemplate', 'status'],
        queryFn: () => getMe('coaTemplate/status'),
    })

    const { data: listData } = useQuery({
        queryKey: ['coaTemplate', 'list'],
        queryFn: () => getMe('coaTemplate/list'),
    })

    const applyMutation = useMutation({
        mutationFn: (templateId) => postMe('coaTemplate/apply', { templateId }),
        onSuccess: (result) => {
            if (result?.error) return
            queryClient.invalidateQueries({ queryKey: ['coaTemplate', 'status'] })
            queryClient.invalidateQueries({ queryKey: ['glAccount'] })
        }
    })

    const status = statusData?.data
    const templates = Array.isArray(listData?.data) ? listData.data : []

    // Pre-select the country's default chart once the list arrives, so the
    // common case is one click. Only while nothing has been chosen.
    useEffect(() => {
        const preferred = (listData?.data ?? []).find(tpl => tpl.isDefault)
        if (preferred) setSelectedTemplateId(current => current || preferred.id)
    }, [listData])

    return (
        <Form.Section width="756px">
                {statusLoading ? (
                    <div>…</div>
                ) : status?.applied ? (
                    <div>
                        {m('accountingSettings.applied', [status.templateName || status.templateId, status.count])}
                    </div>
                ) : (
                    <>
                        <StatusBox>{t('accountingSettings.notApplied')}</StatusBox>
                        <Label>{t('accountingSettings.template.label')}</Label>
                        <Select
                            value={selectedTemplateId}
                            onChange={(e) => setSelectedTemplateId(e.target.value)}
                        >
                            <option value="">{t('accountingSettings.template.placeholder')}</option>
                            {templates.map(tpl => (
                                <option key={tpl.id} value={tpl.id}>
                                    {tpl.name} ({tpl.country}){tpl.isDefault ? ` — ${t('accountingSettings.template.default')}` : ''}
                                </option>
                            ))}
                        </Select>
                        <Button
                            disabled={!selectedTemplateId || applyMutation.isPending}
                            onClick={() => applyMutation.mutate(selectedTemplateId)}
                        >
                            {applyMutation.isPending ? t('accountingSettings.creating') : t('accountingSettings.create')}
                        </Button>
                        {applyMutation.data?.error && (
                            <ErrorText>{t(applyMutation.data.error)}</ErrorText>
                        )}
                    </>
                )}
        </Form.Section>
    )
}

export default AccountingSettingsPage
